import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { EditOperator } from "../components/operators/EditOperator";
import { InviteOperator } from "../components/operators/InviteOperator";
import { IssuedLinkPanel } from "../components/operators/IssuedLinkPanel";
import { PageHeader } from "../components/shell/PageHeader";
import { Button } from "../components/ui/Button";
import { EmptyState, ErrorNotice, Notice, SkeletonRows } from "../components/ui/Feedback";
import { Pill } from "../components/ui/Status";
import type { Column } from "../components/ui/Table";
import { Cell, TableFrame, TableHead, TableRow } from "../components/ui/Table";
import { Metric } from "../components/ui/Text";
import { get, post } from "../lib/api";
import { count, timestamp } from "../lib/format";
import { linkPurposeLabel, lockoutReason, operatorStatusTone } from "../lib/operator";
import { groupPermissions, permissionCount } from "../lib/permissions";
import { useSession } from "../lib/session";
import type { IssuedLink, Operator } from "../lib/types";

/**
 * The people who can sign in to this panel.
 *
 * ## Why this page exists
 *
 * Until it did, the only way to add an operator was a shell on the server and
 * `pnpm admin:create-user`. That is still how the *first* account is made — there
 * is nobody yet to make it from here — but every one after that is an admin action
 * like any other, audited under the name of whoever took it.
 *
 * ## Links, not passwords
 *
 * Nobody on this page ever sees or types another person's password. An invitation
 * and a reset are both a one-time link: the panel shows it once, the admin passes
 * it on, and its owner chooses their own password. The server keeps only a hash of
 * the token, which is why a lost link is replaced rather than looked up.
 *
 * ## What it refuses
 *
 * Anything that would lock the deployment out of its own panel: disabling
 * yourself, removing the Administrator role from yourself, or leaving no active
 * Administrator at all.
 *
 * ## Access is roles plus direct permissions
 *
 * The row shows the roles by name and a count of what they add up to; the count
 * opens into the full list, because "why can this person do X" is answered by the
 * effective set, not by guessing what a role name implies. Every button is gated
 * by its own permission, so an operator may, say, issue reset links without being
 * able to change anyone's access. The
 * buttons say why before they are pressed; the server refuses again if they are.
 *
 * No delete. The audit log names operators by id, and an account that has acted is
 * part of the record of what was done. Disabling is the off switch.
 */

const COLUMNS: readonly Column[] = [
  { key: "operator", label: "Operator", width: "26%" },
  { key: "access", label: "Access", width: "16%" },
  { key: "status", label: "Status", width: "10%" },
  { key: "signedIn", label: "Last sign-in", width: "15%" },
  { key: "link", label: "Open link", width: "15%" },
];

const ACTION_COLUMN: Column = { key: "act", label: "", width: "18%" };

/**
 * Account timestamps are not station-local: an operator belongs to no depot, the
 * same reasoning `Customers` applies.
 */
const UTC = "UTC";

type Panel =
  | { readonly kind: "none" }
  | { readonly kind: "invite" }
  | { readonly kind: "edit"; readonly id: string }
  | { readonly kind: "link"; readonly recipient: string; readonly link: IssuedLink };

export function Operators() {
  const queryClient = useQueryClient();
  const { principal, can } = useSession();
  const [panel, setPanel] = useState<Panel>({ kind: "none" });
  const [notice, setNotice] = useState<string | undefined>(undefined);

  const operators = useQuery({
    queryKey: ["operators"],
    queryFn: () => get<readonly Operator[]>("/operators"),
  });

  const rows = operators.data ?? [];
  const selfId = principal?.type === "user" ? principal.id : undefined;

  const setStatus = useMutation({
    mutationFn: ({ id, next }: { id: string; next: "disable" | "enable" }) =>
      post<Operator>(`/operators/${id}/${next}`, {}),
    onSuccess: (operator) => {
      setNotice(
        operator.status === "disabled"
          ? `${operator.email} disabled. They have been signed out everywhere and any open link no longer works.`
          : `${operator.email} enabled.`,
      );
      void queryClient.invalidateQueries({ queryKey: ["operators"] });
    },
  });

  const issueLink = useMutation({
    mutationFn: (operator: Operator) => post<IssuedLink>(`/operators/${operator.id}/sign-in-link`),
    onSuccess: (link, operator) => {
      setNotice(undefined);
      setPanel({ kind: "link", recipient: operator.email, link });
      void queryClient.invalidateQueries({ queryKey: ["operators"] });
    },
  });

  const editing = panel.kind === "edit" ? rows.find((row) => row.id === panel.id) : undefined;

  return (
    <>
      <PageHeader
        eyebrow="Configuration"
        title="Operators"
        metrics={
          <>
            <Metric label="Active" value={count(countBy(rows, "active"))} hint="can sign in" />
            <Metric
              label="Invited"
              value={count(countBy(rows, "invited"))}
              tone={countBy(rows, "invited") > 0 ? "ink" : "muted"}
              hint="no password yet"
            />
            <Metric
              label="Disabled"
              value={count(countBy(rows, "disabled"))}
              tone={countBy(rows, "disabled") > 0 ? "signal" : "muted"}
            />
          </>
        }
        actions={
          panel.kind === "invite" || !can("operators.invite") ? null : (
            <Button
              variant="primary"
              onClick={() => {
                setNotice(undefined);
                setPanel({ kind: "invite" });
              }}
            >
              Invite operator
            </Button>
          )
        }
        lede="Everyone who can sign in to this panel, and what each of them may do. New operators get a one-time link to choose their own password — nobody here ever sees it."
      />

      {panel.kind === "invite" ? (
        <InviteOperator
          onInvited={({ operator, link }) =>
            setPanel({ kind: "link", recipient: operator.email, link })
          }
          onCancel={() => setPanel({ kind: "none" })}
        />
      ) : null}

      {panel.kind === "link" ? (
        <IssuedLinkPanel
          link={panel.link}
          recipient={panel.recipient}
          onDismiss={() => setPanel({ kind: "none" })}
        />
      ) : null}

      {editing === undefined ? null : (
        <EditOperator
          // Keyed so switching from one operator's edit to another's starts from
          // that operator's values rather than the previous form's.
          key={editing.id}
          operator={editing}
          operators={rows}
          selfId={selfId}
          onSaved={(operator) => {
            setPanel({ kind: "none" });
            setNotice(`${operator.email} updated.`);
          }}
          onCancel={() => setPanel({ kind: "none" })}
        />
      )}

      {notice === undefined ? null : <Notice tone="ok">{notice}</Notice>}
      {operators.isError ? (
        <ErrorNotice error={operators.error} onRetry={() => void operators.refetch()} />
      ) : null}
      {setStatus.isError ? <ErrorNotice error={setStatus.error} /> : null}
      {issueLink.isError ? <ErrorNotice error={issueLink.error} /> : null}

      <div className="flex flex-col gap-3">
        <TableFrame>
          <TableHead columns={[...COLUMNS, ACTION_COLUMN]} />
          {operators.isPending ? (
            <SkeletonRows rows={4} columns={COLUMNS.length + 1} />
          ) : (
            <tbody>
              {rows.map((row, index) => (
                <OperatorRow
                  key={row.id}
                  row={row}
                  index={index}
                  isSelf={row.id === selfId}
                  canEdit={can("operators.update") || can("operators.assign_roles")}
                  canIssueLink={can("operators.issue_link")}
                  canDisable={can("operators.disable")}
                  canEnable={can("operators.enable")}
                  disableBlockedBy={lockoutReason(rows, selfId, { kind: "disable", id: row.id })}
                  isStatusPending={setStatus.isPending && setStatus.variables?.id === row.id}
                  isLinkPending={issueLink.isPending && issueLink.variables?.id === row.id}
                  onEdit={() => {
                    setNotice(undefined);
                    setPanel({ kind: "edit", id: row.id });
                  }}
                  onSetStatus={(next) => setStatus.mutate({ id: row.id, next })}
                  onIssueLink={() => issueLink.mutate(row)}
                />
              ))}
            </tbody>
          )}
        </TableFrame>

        {!operators.isPending && !operators.isError && rows.length === 0 ? (
          <EmptyState
            title="No operator accounts."
            detail="If you are reading this with a service token, the first account still has to be made on the server with `pnpm admin:create-user`."
          />
        ) : null}
      </div>
    </>
  );
}

function OperatorRow({
  row,
  index,
  isSelf,
  canEdit,
  canIssueLink,
  canDisable,
  canEnable,
  disableBlockedBy,
  isStatusPending,
  isLinkPending,
  onEdit,
  onSetStatus,
  onIssueLink,
}: {
  readonly row: Operator;
  readonly index: number;
  readonly isSelf: boolean;
  readonly canEdit: boolean;
  readonly canIssueLink: boolean;
  readonly canDisable: boolean;
  readonly canEnable: boolean;
  readonly disableBlockedBy: string | undefined;
  readonly isStatusPending: boolean;
  readonly isLinkPending: boolean;
  readonly onEdit: () => void;
  readonly onSetStatus: (next: "disable" | "enable") => void;
  readonly onIssueLink: () => void;
}) {
  const disabled = row.status === "disabled";

  return (
    <TableRow index={index} isFaulted={disabled}>
      <Cell>
        <div className="flex flex-col gap-0.5">
          <span className="text-ink">
            {row.name}
            {isSelf ? <span className="ml-2 text-ink-faint">(you)</span> : null}
          </span>
          <span className="text-micro tracking-normal normal-case text-ink-faint">{row.email}</span>
        </div>
      </Cell>
      <Cell>
        <OperatorAccess row={row} />
      </Cell>
      <Cell>
        <Pill tone={operatorStatusTone(row.status)}>{row.status}</Pill>
      </Cell>
      <Cell muted={row.lastLoginAt === null}>
        {row.lastLoginAt === null ? "never" : timestamp(row.lastLoginAt, UTC)}
      </Cell>
      <Cell muted={row.pendingLink === null}>
        {row.pendingLink === null ? (
          "—"
        ) : (
          <div className="flex flex-col gap-0.5">
            <span>{linkPurposeLabel(row.pendingLink.purpose)}</span>
            <span className="text-micro tracking-normal normal-case text-ink-faint">
              until {timestamp(row.pendingLink.expiresAt, UTC)}
            </span>
          </div>
        )}
      </Cell>
      <Cell>
        <div className="flex flex-wrap gap-2">
          {canEdit ? (
            <Button variant="ghost" onClick={onEdit}>
              Edit
            </Button>
          ) : null}
          {/* A disabled account gets no link: the server refuses one, and a link
              that opened onto "this account is disabled" would be a dead end
              handed to a person. Enable first, then issue. */}
          {disabled || !canIssueLink ? null : (
            <Button variant="ghost" pending={isLinkPending} onClick={onIssueLink}>
              {row.status === "invited" ? "New invite link" : "Reset link"}
            </Button>
          )}
          {(disabled ? canEnable : canDisable) ? (
            <Button
              variant={disabled ? "secondary" : "danger"}
              pending={isStatusPending}
              disabled={!disabled && disableBlockedBy !== undefined}
              onClick={() => onSetStatus(disabled ? "enable" : "disable")}
            >
              {disabled ? "Enable" : "Disable"}
            </Button>
          ) : null}
        </div>
        {/* Said on the row rather than in a `title`: a disabled button fires no
            pointer events in most browsers, so its tooltip is never seen. */}
        {!disabled && canDisable && disableBlockedBy !== undefined ? (
          <span className="mt-1 block text-micro tracking-normal normal-case text-ink-faint">
            {disableBlockedBy}
          </span>
        ) : null}
      </Cell>
    </TableRow>
  );
}

/**
 * Roles by name, then the effective count as a disclosure. Native `<details>`:
 * it is keyboard-operable and announced as expandable without any of this code,
 * and the list inside is only read when somebody is asking a specific question.
 */
function OperatorAccess({ row }: { readonly row: Operator }) {
  const roleLine =
    row.roles.length === 0 ? "No roles" : row.roles.map((role) => role.name).join(" · ");

  return (
    <div className="flex flex-col gap-0.5">
      <span className={row.roles.length === 0 ? "text-ink-faint" : "text-ink"}>{roleLine}</span>
      {row.permissions.length === 0 ? null : (
        <span className="text-micro tracking-normal normal-case text-ink-faint">
          + {row.permissions.length} direct
        </span>
      )}
      <details className="text-micro tracking-normal normal-case text-ink-faint">
        <summary className="cursor-pointer hover:text-ink">
          {permissionCount(row.effectivePermissions)} permissions
        </summary>
        <ul className="m-0 mt-1 flex list-none flex-col gap-1 p-0">
          {groupPermissions(row.effectivePermissions).map((group) => (
            <li key={group.group}>
              <span className="font-semibold text-ink-muted">{group.group}:</span>{" "}
              {group.permissions.map((info) => info.label).join(", ")}
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}

function countBy(rows: readonly Operator[], status: Operator["status"]): number {
  return rows.filter((row) => row.status === status).length;
}
